import { r3LabsService, type ActiveSlot, type UpcomingSlot } from '../lib/r3dlabs';
import { EventDetailsModal } from './EventDetailsModal';
import { twitchService } from '../lib/twitch';
import { formatSlotDuration } from '../utils/formatters';
import { EventSlotManager } from '../services/EventSlotManager';
import './EventsComponent.css';

export class EventsComponent {
  private container: HTMLElement;
  private refreshInterval: number | null = null;
  private searchInput!: HTMLInputElement;
  private isRefreshing: boolean = false;
  private modal: EventDetailsModal;
  private readonly REFRESH_INTERVAL = 30000; // Refresh every 30 seconds
  private readonly PLACEHOLDER_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSI0MDAiIGZpbGw9IiMxQTFBMUEiLz48cGF0aCBkPSJNMTgwIDIwMEgyMjBNMTUwIDE3MEgxNzBNMjMwIDE3MEgyNTBNMTUwIDIzMEMyMDAgMjYwIDIwMCAyNjAgMjUwIDIzMCIgc3Ryb2tlPSIjOTE0N0ZGIiBzdHJva2Utd2lkdGg9IjEwIiBzdHJva2UtbGluZWNhcD0icm91bmQiLz48L3N2Zz4=';

  constructor() {
    this.container = document.createElement('div');
    this.container.className = 'events-container';
    this.modal = EventDetailsModal.getInstance();
    this.initializeEvents();
  }

  public async initializeEvents(): Promise<void> {
    // Clear any existing interval first
    this.stopAutoRefresh();
    
    try {
      await this.refreshEvents();
      this.startAutoRefresh();
    } catch (error) {
      console.error('Failed to initialize events component:', error);
      this.container.innerHTML = '<div class="no-events">Failed to load events. Will retry shortly...</div>';
      // Retry initialization after a delay
      setTimeout(() => this.initializeEvents(), 5000);
    }
  }

  private startAutoRefresh(): void {
    // Clear any existing interval first
    this.stopAutoRefresh();
    
    this.refreshInterval = window.setInterval(async () => {
      try {
        if (!this.isRefreshing) {
          await this.refreshEvents();
        } else {
          console.log('Events: Skipping refresh as previous refresh is still in progress');
        }
      } catch (error) {
        console.error('Error in auto-refresh:', error);
        // Stop the interval on error and attempt to restart
        this.stopAutoRefresh();
        setTimeout(() => this.startAutoRefresh(), 5000);
      }
    }, this.REFRESH_INTERVAL);
  }

  private stopAutoRefresh(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  private formatDateTime(dateTimeStr: string): string {
    const date = new Date(dateTimeStr + 'Z');
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).replace(',', '').replace(/\s+/g, ' ');
  }

  private getEventImageUrl(imageId: string | null): string {
    if (!imageId) return this.PLACEHOLDER_IMAGE;
    return `${r3LabsService.baseUrl}/images/${imageId}`;
  }

  private createActiveEventElement(activeSlot: ActiveSlot, isGracePeriod: boolean): HTMLElement {
    const activeEvent = document.createElement('div');
    activeEvent.className = 'active-event';

    if (isGracePeriod) {
      activeEvent.classList.add('grace-period');
    }

    const imageUrl = this.getEventImageUrl(activeSlot.event_image_id);
    
    activeEvent.addEventListener('click', () => {
      const eventId = activeSlot.event_id || activeSlot.train_event_id;
      if (eventId && eventId !== 'unknown') {
        this.modal.show(eventId);
      } else {
        console.warn('No valid event_id available for active slot:', {
          event_id: activeSlot.event_id,
          train_event_id: activeSlot.train_event_id,
          slot: activeSlot
        });
      }
    });
    
    activeEvent.innerHTML = `
      <div class="active-status">${isGracePeriod ? 'Previous Slot' : 'This slot is LIVE - You are active'}</div>
      <div class="event-content">
        <div class="event-image-container">
          <img 
            src="${imageUrl}" 
            alt="${activeSlot.event_name}"
            class="event-image"
            onerror="this.src='${this.PLACEHOLDER_IMAGE}'"
          >
        </div>
        <div class="event-details">
          <div class="event-name">${activeSlot.event_name}</div>
          <div class="event-time">
            ${this.formatDateTime(activeSlot.active_slot_start_datetime)}
          </div>
          <div class="event-duration">
            Duration: ${formatSlotDuration(activeSlot.active_slot_start_datetime, activeSlot.active_slot_end_datetime)}
          </div>
          <div class="ondeck-channel">
            <svg class="user-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="currentColor"/>
            </svg>
            ${activeSlot.ondeck_channel_name ? `<strong>${activeSlot.ondeck_channel_name}</strong> is your raid target` : 'No raid target'}
          </div>
        </div>
      </div>
    `;

    return activeEvent;
  }

  private async refreshEvents(): Promise<void> {
    if (this.isRefreshing) {
      console.log('Events: Refresh already in progress, skipping');
      return;
    }
    
    this.isRefreshing = true;
    const refreshButton = this.container.querySelector('.refresh-button') as HTMLButtonElement;
    if (refreshButton) {
      refreshButton.disabled = true;
      refreshButton.classList.add('loading');
    }
    try {
      const user = await twitchService.getUser();
      if (!user) {
        const eventsContent = document.createElement('div');
        eventsContent.innerHTML = '<div class="no-events">Please log in to view events</div>';
        this.updateContent(eventsContent);
        return;
      }

      const events = await r3LabsService.getUserEvents(user.login);
      const eventsContent = document.createElement('div');

      let slotToShow: ActiveSlot | null = null;
      let isGracePeriod = false;

      // First check if we have an active slot from r3dlabs
      if (events.active_slot) {
        slotToShow = events.active_slot;
        isGracePeriod = false;
        
        // Store in EventSlotManager
        EventSlotManager.storeActiveSlot(
          events.active_slot.active_slot_id,
          {
            event_id: events.active_slot.event_id || events.active_slot.train_event_id || '',
            event_name: events.active_slot.event_name,
            event_slug: events.active_slot.event_slug,
            event_image_id: events.active_slot.event_image_id,
            active_slot_start_datetime: events.active_slot.active_slot_start_datetime,
            active_slot_end_datetime: events.active_slot.active_slot_end_datetime,
            ondeck_channel_name: events.active_slot.ondeck_channel_name,
            ondeck_channel_platform_user_id: events.active_slot.ondeck_channel_platform_user_id
          }
        );
      } 
      // Then check if we have a slot in grace period from cache
      else {
        const cachedSlot = EventSlotManager.getLastActiveSlot();
        
        if (cachedSlot) {
          // Convert cached slot data back to ActiveSlot format
          const slot: ActiveSlot = {
            event_id: cachedSlot.event_id,
            event_name: cachedSlot.event_name,
            event_slug: cachedSlot.event_slug,
            event_image_id: cachedSlot.event_image_id,
            active_slot_id: '', // Will be set below
            active_slot_start_datetime: cachedSlot.active_slot_start_datetime,
            active_slot_end_datetime: cachedSlot.active_slot_end_datetime,
            next_slot_start_datetime: null,
            next_slot_end_datetime: null,
            active_channel_name: '',
            active_channel_platform_user_id: '',
            ondeck_channel_name: cachedSlot.ondeck_channel_name,
            ondeck_channel_platform_user_id: cachedSlot.ondeck_channel_platform_user_id
          };
          
          slotToShow = slot;
          isGracePeriod = true;
          console.log('Events: Using cached slot in grace period');
        } else {
          console.log('Events: No active or cached slots found');
        }
      }

      // Create the active section if we have a slot to show
      if (slotToShow) {
        const activeSection = document.createElement('div');
        activeSection.className = 'events-section';
        activeSection.appendChild(this.createActiveEventElement(slotToShow, isGracePeriod));
        eventsContent.appendChild(activeSection);
      }

      // Create upcoming section
      const upcomingSection = document.createElement('div');
      upcomingSection.className = 'events-section';
      
      // Add section title
      const title = document.createElement('h3');
      title.textContent = 'Upcoming Events';
      upcomingSection.appendChild(title);
      
      // Create filter section if it doesn't exist
      if (!this.searchInput) {
        const filterSection = document.createElement('div');
        filterSection.className = 'events-filter';
        
        this.searchInput = document.createElement('input');
        this.searchInput.type = 'text';
        this.searchInput.placeholder = 'Search upcoming events...';
        this.searchInput.addEventListener('input', this.handleFilter.bind(this));
        
        const refreshButton = document.createElement('button');
        refreshButton.className = 'refresh-button';
        refreshButton.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
          </svg>
        `;
        refreshButton.addEventListener('click', async (e) => {
          const button = e.currentTarget as HTMLButtonElement;
          button.disabled = true;
          button.classList.add('loading');
          await this.refreshEvents();
          button.disabled = false;
          button.classList.remove('loading');
        });
        
        filterSection.appendChild(this.searchInput);
        filterSection.appendChild(refreshButton);
        upcomingSection.appendChild(filterSection);
      } else {
        // Reuse existing filter section
        const filterSection = this.searchInput.closest('.events-filter');
        if (filterSection) {
          upcomingSection.appendChild(filterSection);
        }
      }

      if (events.upcoming_slots && events.upcoming_slots.length > 0) {
        const upcomingContainer = document.createElement('div');
        upcomingContainer.className = 'upcoming-events';
        
        // Show all slots initially
        events.upcoming_slots.forEach((slot: UpcomingSlot) => {
          upcomingContainer.appendChild(this.createUpcomingEventElement(slot));
        });
        upcomingSection.appendChild(upcomingContainer);
      } else {
        const noEventsContainer = document.createElement('div');
        noEventsContainer.className = 'no-events';
        
        const noEventsText = document.createElement('div');
        noEventsText.textContent = 'No upcoming slots found';
        noEventsContainer.appendChild(noEventsText);
        
        const eventSource = document.createElement('div');
        eventSource.className = 'event-source';
        eventSource.style.fontSize = '0.8em';
        eventSource.style.marginTop = '8px';
        eventSource.style.opacity = '0.7';
        eventSource.innerHTML = 'Event Source <a href="https://r3dlabs.com" target="_blank" rel="noopener noreferrer" style="color: #9147ff; text-decoration: none;">R3dLabs.com</a>';
        noEventsContainer.appendChild(eventSource);
        
        upcomingSection.appendChild(noEventsContainer);
      }

      eventsContent.appendChild(upcomingSection);
      this.updateContent(eventsContent);
      
      // Apply filter after content update if there's a filter value
      if (this.searchInput?.value) {
        this.handleFilter();
      }

      // Clean up old slots
      EventSlotManager.cleanupOldSlots();
    } catch (error) {
      console.error('Error refreshing events:', error);
      this.container.innerHTML = '<div class="no-events">Failed to load events. Will retry shortly...</div>';
      // Stop the interval and attempt to restart
      this.stopAutoRefresh();
      setTimeout(() => this.startAutoRefresh(), 5000);
    } finally {
      this.isRefreshing = false;
      const refreshButton = this.container.querySelector('.refresh-button') as HTMLButtonElement;
      if (refreshButton) {
        refreshButton.disabled = false;
        refreshButton.classList.remove('loading');
      }
    }
  }

  private handleFilter(): void {
    const searchTerm = this.searchInput.value.toLowerCase().trim();
    const upcomingContainer = this.container.querySelector('.upcoming-events');
    const noResultsMessage = this.container.querySelector('.no-results');
    
    if (upcomingContainer) {
      let visibleCount = 0;
      Array.from(upcomingContainer.children).forEach(child => {
        const eventName = child.querySelector('.event-name')?.textContent?.toLowerCase() || '';
        const channelName = child.querySelector('.event-channel')?.textContent?.toLowerCase() || '';
        const matches = !searchTerm || 
          eventName.includes(searchTerm) || 
          channelName.includes(searchTerm);
        (child as HTMLElement).style.display = matches ? '' : 'none';
        if (matches) visibleCount++;
      });

      // Handle no results message
      if (noResultsMessage) {
        (noResultsMessage as HTMLElement).style.display = visibleCount === 0 ? '' : 'none';
      } else if (visibleCount === 0) {
        const message = document.createElement('div');
        message.className = 'no-events no-results';
        message.textContent = 'No matching events found';
        upcomingContainer.parentElement?.appendChild(message);
      }
    }
  }

  private createUpcomingEventElement(upcomingSlot: UpcomingSlot): HTMLElement {
    const upcomingEvent = document.createElement('div');
    upcomingEvent.className = 'upcoming-event';

    const imageUrl = this.getEventImageUrl(upcomingSlot.event_image_id);

    
    upcomingEvent.addEventListener('click', () => {
      const eventId = upcomingSlot.event_id || upcomingSlot.train_event_id;
      if (eventId && eventId !== 'unknown') {
        this.modal.show(eventId);
      } else {
        console.warn('No valid event_id available for upcoming slot:', {
          event_id: upcomingSlot.event_id,
          train_event_id: upcomingSlot.train_event_id,
          slot: upcomingSlot
        });
      }
    });
    
    upcomingEvent.innerHTML = `
      <div class="event-content">
        <div class="event-image-container">
          <img 
            src="${imageUrl}" 
            alt="${upcomingSlot.event_name}"
            class="event-image"
            onerror="this.src='${this.PLACEHOLDER_IMAGE}'"
          >
        </div>
        <div class="event-details">
          <div class="event-name">${upcomingSlot.event_name}</div>
          <div class="event-time">
            ${this.formatDateTime(upcomingSlot.slot_start_datetime)}
          </div>
          <div class="event-duration">
            Duration: ${formatSlotDuration(upcomingSlot.slot_start_datetime, upcomingSlot.slot_end_datetime)}
          </div>
        </div>
      </div>
    `;

    return upcomingEvent;
  }

  private updateContent(newContent: HTMLElement): void {
    // Preserve the filter section
    const filterSection = this.container.querySelector('.events-filter');
    
    // Clear the container
    this.container.innerHTML = '';
    
    // Add all the children from the new content
    while (newContent.firstChild) {
      this.container.appendChild(newContent.firstChild);
    }
    
    // If we had a filter section and it wasn't in the new content, add it back
    if (filterSection && !this.container.querySelector('.events-filter')) {
      const upcomingSection = this.container.querySelector('.events-section:last-child');
      if (upcomingSection) {
        upcomingSection.insertBefore(filterSection, upcomingSection.querySelector('.upcoming-events'));
      }
    }
  }

  public getElement(): HTMLElement {
    return this.container;
  }

  public destroy(): void {
    // Stop auto refresh
    this.stopAutoRefresh();
    
    // Clean up modal
    if (this.modal) {
      this.modal.destroy();
    }
    
    // Create new modal instance for next initialization
    this.modal = EventDetailsModal.getInstance();
  }
}
